import {Trans} from '@lingui/macro';
import React, {FC, useMemo, useState} from 'react';
import styled from 'styled-components';

import {useInitialEffect} from '../../../libs/shared/helpers/src/react/hooks/useEffectHelpers';
import {Handler, HandlerOf, PredicateOf} from '../../../libs/shared/helpers/src/react/reactHelpers';
import {Button} from '../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {useNavigationBlocking} from '../../../srcCommon/app/navigationBlocking';
import {BusinessHoursContent} from '../../analytics/components/businessHours/businessHoursContent';
import {BusinessHoursDialogConfirmation} from '../../analytics/components/businessHours/businessHoursDialogConfirmation';
import {
  getBusinessHoursClearConfirmLabel,
  getBusinessHoursClearDialogBody,
  getBusinessHoursClearDialogTitle,
  getBusinessHoursSaveConfirmLabel,
  getBusinessHoursSaveDialogBody,
  getBusinessHoursSaveDialogTitle,
  hasAnyBusinessHour,
} from '../../analytics/helpers/businessHoursHelpers';
import {findTimeZone, TimeFormatsEnum} from '../../helpers/dateTimeHelpers';
import {useAppSelector} from '../../helpers/reduxHelpers';
import {BusinessHours} from '../../models/api/businessHoursModel';
import {CalendarWeekDaysEnum} from '../../models/api/calendarCommonTypes';
import {CompanyFeaturesEnum} from '../../models/api/companyModel';
import {InboxModel} from '../../models/api/inboxModel';
import {TeamModel} from '../../models/api/teamModel';
import {buildSettingsPath} from '../../models/url/settingsUrlHelpers';
import {
  SettingsInboxesSubPagesEnum,
  SettingsInboxesUrl,
  SettingsResourcePagesEnum,
} from '../../models/url/settingsUrlModel';
import {makeUserIsAdminForTeamNamespace} from '../../store/data/teams/teamsSelectors';
import {SettingsPanelId} from '../../store/panels/panelStateBase';
import {SettingsDiscardChangesDialog} from '../common/settingsDiscardChangesDialog';
import {
  SettingsFooter,
  StyledSettingsContent,
  StyledSettingsContentWrap,
  StyledSettingsWrapper,
} from '../common/settingsWrapper';
import {useSettingsInboxesBanners} from '../inboxes/settingsInboxesCommon';
import {SettingsBusinessHoursEmptyState} from './settingsBusinessHoursEmptyState';
import {useInboxBusinessHoursInfoBox} from './settingsBusinessHoursInfoBox';
import {SettingsBusinessHoursWarningBanner} from './settinsBusinessHoursWarningBanner';

/*
 * Props.
 */

interface SettingsBusinessHoursProps {
  panelId: SettingsPanelId;
  inbox: InboxModel | undefined;
  team: TeamModel | undefined;
  onSave: HandlerOf<InboxModel>;
  calendarWeekStartDay: CalendarWeekDaysEnum;
  timeFormat: TimeFormatsEnum;
  url: SettingsInboxesUrl;
  navigateToLink: HandlerOf<string>;
  findHasCompanyFeature: PredicateOf<CompanyFeaturesEnum>;
  onInboxUpdate: (inbox: InboxModel) => Promise<void>;
}

/*
 * Style.
 */

const StyledBusinessHoursDiv = styled.div`
  padding-right: 50px;
`;

const StyledBusinessHoursWrapperDiv = styled.div`
  display: flex;
  align-items: flex-start;
`;

const StyledBannerAndBhWrapperDiv = styled.div`
  flex: 1;
`;

/*
 * Constants.
 */

const defaultTimezone = findTimeZone();

const emptyBusinessHours: BusinessHours = {
  enabled: false,
  tz: defaultTimezone,
  mon: null,
  tue: null,
  wed: null,
  thu: null,
  fri: null,
  sat: null,
  sun: null,
};

/*
 * Component.
 */

export const SettingsBusinessHours: FC<SettingsBusinessHoursProps> = ({
  panelId,
  inbox,
  team,
  onSave,
  calendarWeekStartDay,
  timeFormat,
  url,
  navigateToLink,
  findHasCompanyFeature,
  onInboxUpdate,
}) => {
  const businessHours = inbox?.businessHours ?? emptyBusinessHours;

  const userIsAdminForTeamNamespace = useMemo(
    () => makeUserIsAdminForTeamNamespace(url.namespace),
    [url.namespace],
  );
  const isUserTeamAdmin = useAppSelector(userIsAdminForTeamNamespace);

  const [initialBusinessHours, setInitialBusinessHours] = useState(businessHours);
  const [currentBusinessHours, setCurrentBusinessHours] = useState(businessHours);

  const [confirmEdit, setConfirmEdit] = useState(false);
  const [confirmDisable, setConfirmDisable] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  const {showBusinessHoursUpdateBanner} = useSettingsInboxesBanners();
  const {infoBox} = useInboxBusinessHoursInfoBox();

  const {didBlockNavigation, cancelNavigation, unblockNavigation} = useNavigationBlocking(panelId, isDirty);

  const hasCurrentBusinessHours = hasAnyBusinessHour(currentBusinessHours); // Used to display empty state.

  const hasTeamsFeature = findHasCompanyFeature(CompanyFeaturesEnum.TEAMS);
  const hasAdvancedRulesFeature = findHasCompanyFeature(CompanyFeaturesEnum.ADVANCED_RULES);
  const hasAnalyticsFeature = findHasCompanyFeature(CompanyFeaturesEnum.ANALYTICS);

  useInitialEffect(() => {
    // Check if user has acces to business hours for this inbox. Redirect to Overview if not.
    // Business Hours are restricted to the Scale plan (must have teams) and to team admins.
    // Company also needs to have access to analytics OR advanced_rules (although it can be redundant with the 'teams' check, I'd rather be explicit here).
    if (
      !inbox ||
      !isUserTeamAdmin ||
      !hasTeamsFeature ||
      (!hasAdvancedRulesFeature && !hasAnalyticsFeature)
    ) {
      navigateToLink(
        buildSettingsPath({
          ...url,
          resourcePage: SettingsResourcePagesEnum.EDIT,
          subPage: SettingsInboxesSubPagesEnum.OVERVIEW,
        }),
      );
    }
  });

  if (!inbox) {
    return null;
  }

  const onBusinessHoursUpdate = (updatedBusinessHours: BusinessHours) => {
    setIsDirty(true);
    setCurrentBusinessHours(updatedBusinessHours);
  };

  const onConfirm = async () => {
    const newBusinessHours = {
      ...currentBusinessHours,
      enabled: hasAnyBusinessHour(currentBusinessHours),
      tz: currentBusinessHours.tz ?? defaultTimezone,
    };
    await onInboxUpdate({
      ...inbox,
      businessHours: newBusinessHours,
    });
    setIsDirty(false);
    setCurrentBusinessHours(newBusinessHours);
    setInitialBusinessHours(newBusinessHours);
    showBusinessHoursUpdateBanner();
  };

  const onDisable = () => {
    onSave({
      ...inbox,
      businessHours: {enabled: false, tz: currentBusinessHours.tz ?? defaultTimezone},
    });
    setIsDirty(false);
    setCurrentBusinessHours(emptyBusinessHours);
    setInitialBusinessHours(emptyBusinessHours);
    showBusinessHoursUpdateBanner();
  };

  const onCancel = () => {
    setCurrentBusinessHours(initialBusinessHours);
    setIsDirty(false);
  };

  const handleConfirmationOK = () => {
    onConfirm();
    setConfirmEdit(false);
    setConfirmDisable(false);
  };

  const handleDisableOk = () => {
    onDisable();
    setConfirmEdit(false);
    setConfirmDisable(false);
  };

  return (
    <StyledSettingsWrapper>
      <StyledSettingsContentWrap>
        <StyledSettingsContent>
          <StyledBusinessHoursDiv>
            <StyledBusinessHoursWrapperDiv>
              <StyledBannerAndBhWrapperDiv>
                <SettingsBusinessHoursWarningBanner
                  inbox={inbox}
                  team={team}
                  navigateToLink={navigateToLink}
                />
                <BusinessHoursContent
                  onUpdate={onBusinessHoursUpdate}
                  onDisable={() => setConfirmDisable(true)}
                  businessHours={currentBusinessHours}
                  calendarWeekStartDay={calendarWeekStartDay}
                  timeFormat={timeFormat}
                />
                <SettingsBusinessHoursEmptyState hasBusinessHours={hasCurrentBusinessHours} />
              </StyledBannerAndBhWrapperDiv>
              {infoBox}
            </StyledBusinessHoursWrapperDiv>
          </StyledBusinessHoursDiv>
        </StyledSettingsContent>
      </StyledSettingsContentWrap>
      {maybeRenderFooter(onCancel, setConfirmEdit, isDirty)}
      {maybeRenderDiscardChangesDialog(didBlockNavigation, cancelNavigation, unblockNavigation)}
      {maybeRenderConfirmEditDialog(confirmEdit, handleConfirmationOK, setConfirmEdit, inbox.name)}
      {maybeRenderConfirmDisableDialog(confirmDisable, handleDisableOk, setConfirmDisable, inbox.name)}
    </StyledSettingsWrapper>
  );
};

/*
 * Helpers.
 */

const maybeRenderFooter = (onCancel: Handler, setConfirmEdit: HandlerOf<boolean>, isDirty: boolean) => {
  if (!isDirty) {
    return null;
  }

  return (
    <SettingsFooter>
      <Button variant={ButtonVariantsEnum.SECONDARY} onClick={onCancel}>
        <Trans id="settings-business-hours-cancel">Cancel</Trans>
      </Button>
      <Button variant={ButtonVariantsEnum.PRIMARY} onClick={() => setConfirmEdit(true)}>
        <Trans id="settings-business-hours-save-changes">Save</Trans>
      </Button>
    </SettingsFooter>
  );
};

const maybeRenderDiscardChangesDialog = (
  didBlockNavigation: boolean,
  cancelNavigation: Handler,
  unblockNavigation: Handler,
) => {
  if (!didBlockNavigation) {
    return null;
  }
  return <SettingsDiscardChangesDialog onCancel={cancelNavigation} onDiscard={unblockNavigation} />;
};

const maybeRenderConfirmEditDialog = (
  confirmEdit: boolean,
  handleConfirmationOK: Handler,
  setConfirmEdit: HandlerOf<boolean>,
  inboxName: string,
) => {
  if (!confirmEdit) {
    return null;
  }
  return (
    <BusinessHoursDialogConfirmation
      onConfirm={handleConfirmationOK}
      onCancel={() => setConfirmEdit(false)}
      title={getBusinessHoursSaveDialogTitle(inboxName)}
      body={getBusinessHoursSaveDialogBody()}
      confirmLabel={getBusinessHoursSaveConfirmLabel()}
    />
  );
};

const maybeRenderConfirmDisableDialog = (
  confirmDisable: boolean,
  handleDisableOk: Handler,
  setConfirmDisable: HandlerOf<boolean>,
  inboxName: string,
) => {
  if (!confirmDisable) {
    return null;
  }
  return (
    <BusinessHoursDialogConfirmation
      onConfirm={handleDisableOk}
      onCancel={() => setConfirmDisable(false)}
      title={getBusinessHoursClearDialogTitle(inboxName)}
      body={getBusinessHoursClearDialogBody()}
      confirmLabel={getBusinessHoursClearConfirmLabel()}
    />
  );
};
